import { supabase } from '../supabase';
import { callGemini } from './providers/gemini';
import { callOpenAI } from './providers/openai';
import { callClaude } from './providers/claude';
import { callMistral } from './providers/mistral';
import { callGroq } from './providers/groq';
import { useStore } from '../../store/useStore';

// Static tracking to avoid spamming APIs after failures
let isDisabledForSession = false;
const knownInvalidKeys = new Set<string>();

/**
 * Neural Reflection Service
 * 
 * Instead of running a 2GB model locally, we use a lightweight background loop.
 * After every Cloud AI response, we trigger a "reflection" turn that extracts
 * key facts, user preferences, and situational context into the `ai_memory` table.
 */
export async function triggerNeuralReflection(
  userInput: string,
  aiResponse: string,
  userId: string,
  currentProvider?: string
) {
  // 1. Session Status Check
  if (isDisabledForSession) {
    return;
  }

  // Run in background
  (async () => {
    try {
      // 2. Provider Selection
      const store = useStore.getState();
      const currentUser = store.currentUser;
      const apiKeys = currentUser?.user_api_keys || {};
      
      // Try current provider first if it has a key
      let activeProvider = currentProvider;
      let apiKey = activeProvider ? apiKeys[activeProvider] : null;

      // Fallback to Gemini platform key if no user key
      if (!apiKey) {
        const platformGeminiKey = (import.meta as any).env.VITE_GEMINI_API_KEY;
        if (platformGeminiKey) {
          activeProvider = 'gemini';
          apiKey = platformGeminiKey;
        }
      }

      const key = Array.isArray(apiKey) ? apiKey[0] : apiKey;
      if (!key || typeof key !== 'string') {
        return;
      }

      const trimmedKey = key.trim();
      if (!trimmedKey || 
          trimmedKey.includes('placeholder') || 
          trimmedKey.includes('YOUR_') || 
          trimmedKey.length < 10) {
        return;
      }

      if (knownInvalidKeys.has(trimmedKey)) {
        return;
      }

      // Check specific provider patterns
      if (activeProvider === 'gemini') {
        if (!trimmedKey.startsWith('AIzaSy') || trimmedKey.length < 35) {
          return;
        }
      }

      console.log('[Neural Reflection] Starting background loop...');
      
      const reflectionPrompt = `
      Extract key information from this real estate conversation to help personalized future interactions.
      
      USER: "${userInput}"
      AI: "${aiResponse}"
      
      Output exactly one valid JSON object with these fields:
      - facts: Array of strings (new facts about the user or their business)
      - preferences: Object (key-value pairs of learned UI/behavioral preferences)
      - situational_context: String (summary of current focus or emotional state)
      - follow_up_needed: Boolean
      
      If nothing meaningful to extract, return null.
      `;

      // 3. Call the appropriate provider
      let rawReflection: string | null = null;
      const systemPrompt = "You are a data extraction specialist.";

      try {
        switch (activeProvider) {
          case 'gemini':
            rawReflection = await callGemini(reflectionPrompt, key, systemPrompt, undefined, undefined, [], true);
            break;
          case 'openai':
            rawReflection = await callOpenAI(reflectionPrompt, [], key);
            break;
          case 'claude':
            rawReflection = await callClaude(reflectionPrompt, [], key);
            break;
          case 'mistral':
            rawReflection = await callMistral(reflectionPrompt, [], key);
            break;
          case 'groq':
            rawReflection = await callGroq(reflectionPrompt, [], key);
            break;
          default:
            console.log(`[Neural Reflection] Provider ${activeProvider} not supported for reflection fallback.`);
            return;
        }
      } catch (err: any) {
        // Disable for session and mark this key as invalid to prevent future console noise
        isDisabledForSession = true;
        knownInvalidKeys.add(trimmedKey);
        return;
      }

      if (!rawReflection) return;

      const jsonMatch = rawReflection.match(/\{[\s\S]*\}/);
      if (!jsonMatch) return;

      const memoryData = JSON.parse(jsonMatch[0]);
      if (!memoryData) return;

      // Store in Supabase ai_memory
      const { error } = await supabase.from('ai_memory').insert({
        user_id: userId,
        content: userInput + " | " + aiResponse,
        memory_type: 'neural_reflection',
        metadata: memoryData,
        importance: 0.5
      });

      if (error) throw error;
    } catch (err: any) {
      // Fail silently in the background so it doesn't clutter the console with API errors
    }
  })();
}
